<template>
  <core-modal-marketing
    tag="Video Streaming & Editing Access"
    tag-icon="i-ri-flashlight-fill"
    :title="title"
    :subtitle="subtitle"
    header-icon="i-ri-play-circle-line"
  >

    <div v-if="pending" class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
      <core-upsell-product-card-skeleton />
      <core-upsell-product-card-skeleton />
    </div>

    <core-contextual-error-box
      v-else-if="error || !hasProducts"
      size="sm"
      class="border-2 border-gray-200 rounded-md"
      :action="retry"
    >
      <template #heading>Unable to Load Video Plans</template>
      <template #default>Please try again or contact support.</template>
    </core-contextual-error-box>

    <div v-else>
      <u-tabs :items="VIDEO_TABS" :default-index="0">
        <template #item="{ item }">
          <template v-if="item.value === 'permanent'">
            <video-product-group
              :products="data.permanent"
              :is-adding-to-cart="isAddingToCart"
              @add-to-cart="addToCart"
            />
          </template>
          <template v-else>
            <video-product-group
              :products="data.subscription"
              :is-adding-to-cart="isAddingToCart"
              @add-to-cart="addToCart"
            />
          </template>
        </template>
      </u-tabs>

      <core-upsell-features class="mt-8" :items="VIDEO_FEATURES" />

      <p class="mt-8 text-center">
        <u-link
          :to="EXTERNAL_URLS.video"
          external
          class="text-sm text-gray-500 underline"
        >
          Learn More about FOREVER Video Streaming & Editing
        </u-link>
      </p>
    </div>
  </core-modal-marketing>
</template>

<script setup>

  import {VIDEO_PRODUCT_GROUP} from '~/constants/pricing.js';

  const props = defineProps({
    title: {
      type: String,
      default: 'Bring Your Videos to Life'
    },
    subtitle: {
      type: String,
      default: `Create meaningful stories from your memories with simple editing tools and ad-free streaming. Everything you need to preserve, enjoy, and share your videos is right here in FOREVER.`
    },
    upsellTracking: Object
  });

  const VIDEO_TABS = [
    { label: 'Subscription', value: 'subscription' },
    { label: 'Permanent', value: 'permanent' }
  ]

  const VIDEO_FEATURES = [
    {
      title: 'Merge Videos',
      description: 'Combine clips and videos into a single memory.',
      icon: 'i-ri-git-merge-line'
    },
    {
      title: 'Edit Videos Online',
      description: 'Trim clips, create highlights, and add title pages.',
      icon: 'i-ri-scissors-cut-line'
    },
    {
      title: 'Stream Anywhere',
      description: 'High-quality, ad-free streaming that is truly yours.',
      icon: 'i-ri-play-line'
    }
  ];

  const VIDEO_BASE_PRODUCTS = [
    {
      sku: 'VIDEO-LF-060',
      label: '60-Monthly Payments',
      type: 'permanent',
      isMonthly: true,
      addendum: 'Early payoff available. Access starts immediately.',
    },
    {
      sku: 'VIDEO-LF-001',
      label: 'One-Time Investment',
      type: 'permanent',
      addendum: 'Pay Once. Never pay again!',
      featured: true,
    },
    {
      sku: 'VIDEO-MN-001',
      label: 'Month-to-Month',
      type: 'subscription',
      isMonthly: true,
      addendum: 'Monthly subscription. Cancel anytime.',
    },
    {
      sku: 'VIDEO-YR-001',
      label: 'Yearly',
      type: 'subscription',
      addendum: 'Yearly subscription. No long-term commitment.',
      featured: true
    }
  ];

  const {
    products: pricingProducts,
    pending,
    error,
    retry
  } = usePricingGroup({
    groupSlug: VIDEO_PRODUCT_GROUP
  });

  const data = computed(() => {
    const productsForDisplay = {permanent: [], subscription: []};

    VIDEO_BASE_PRODUCTS.forEach((plan) => {
      const product = pricingProducts.value?.find(product => product.sku === plan.sku);
      if (!product) return;

      const percentOff = product.promotionPrice
        ? ((product.price_cents - product.promotionPrice) / product.price_cents) * 100
        : 0;

      const centsOff = product.promotionPrice
        ? (product.price_cents - product.promotionPrice)
        : 0;

      productsForDisplay[plan.type].push({
        ...plan,
        ...product,
        percentOff,
        centsOff
      });
    });

    return productsForDisplay;
  });
  const hasProducts = computed(() =>
    data.value?.permanent?.length && data.value?.subscription?.length
  );

  const isAddingToCart = ref(false);
  async function addToCart(product) {
    if (isAddingToCart.value) return;

    isAddingToCart.value = true;
    try {
      trackUpsellCta({
        tracking: props.upsellTracking,
        product
      });

      await usePricingApi().addToCart({
        productId: product.id,
        promotionCode: product.promotionCode
      });

      window.location = EXTERNAL_URLS.cart;
    } catch {
      useErrorToast().add('Unable to add to cart at this time. Please try again later.');
    } finally {
      isAddingToCart.value = false;
    }
  }

</script>
